// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CaretDown from "@eisinc/XDL/Icons/CaretDown";
import CaretUp from "@eisinc/XDL/Icons/CaretUp";
import IconTableSort from "@eisinc/XDL/Icons/IconTableSort";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { xBaseTheme } from "@eisinc/XDL/types";
import { color, xScreen } from "@eisinc/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import { DataTableCssOverrides } from "@insite/mobius/DataTable/DataTable";
import DataTablePresentationPropsDefault from "@insite/mobius/DataTable/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

const stickyMode = () => css`
    ${xScreen("lte", "sm")} {
        @media not print {
                position: sticky;
            left: 0;
            z-index: 2;
            background-clip: padding-box;
            background: inherit;
            > ${TypographyStyle} {
                display: block;
                max-width: 140px;
                text-overflow: ellipsis;
                overflow: hidden;
            }
        }
    }
`;

export default {
    defaultProps: {
        ...DataTablePresentationPropsDefault,
        mergeCss: true,
        css: css`
            td[class^="DataTableCellBase"] {
                & > ${TypographyStyle}:empty:after {
                    content: "- -";
                }
            }
        `,
        cssOverrides: {
            header: css`
                background: ${color("common.ui2")};
                padding: 8px;
                border-bottom: 0;
                height: initial;
                line-height: normal;
                border-top: 2px solid ${color("common.ui5")};
                font-weight: ${fontConfig.weight.medium};
                font-size: ${fontConfig.xSmall.size}px;
                line-height: ${fontConfig.xSmall.lineHeight};
                color: ${color("common.ui6")};
            `,
            head: css`
                background: ${color("common.ui2")};
                th {
                    border-right: 1px solid ${color("common.border")};
                    > ${StyledButton} {
                        display: flex;
                        justify-content: space-between;
                    }
                }

                th:first-child {
                    ${stickyMode()}
                    border-left: 1px solid ${color("common.border")};
                }
            `,
            cell: css`
                padding: 8px;
                border-bottom: 0;
                height: initial;
                line-height: normal;
                color: ${color("common.ui6")};
                font-size: ${fontConfig.singleLineSmall.size}px;
                line-height: ${fontConfig.singleLineSmall.lineHeight};
                > ${TypographyStyle}, ${StyledButton} > ${TypographyStyle} {
                    font-size: ${fontConfig.singleLineSmall.size}px;
                    line-height: ${fontConfig.singleLineSmall.lineHeight};
                    font-weight: ${fontConfig.weight.regular};
                }

                &:first-child {
                    ${stickyMode()}
                }
            `,
            row: css`
                background: ${color("common.white")};
            `,
            evenRow: css`
                background: ${color("common.ui2")};
            `,
        } as DataTableCssOverrides,
        headerTypographyProps: {
            mergeCss: true,
            ...fontConfig.xSmall,
            weight: fontConfig.weight.medium,
            color: "common.ui6",
            css: css`
                letter-spacing: 0;
            `,
        },
        cellTypographyProps: {
            mergeCss: true,
            ...fontConfig.singleLineRegular,
            weight: fontConfig.weight.regular,
            css: css`
                letter-spacing: 0;
            `,
        },
        sortClickableProps: {
            css: css`
                width: 100%;
                height: 100%;
                &:hover {
                    ${TypographyStyle}, ${IconWrapper} {
                        color: ${color("common.ui8")};
                    }
                }
            `,
        },
        sortIconProps: {
            size: fontConfig.xSmall.size,
            color: "common.ui6",
            css: css`
                margin-left: 4px;
            `,
        },
        sortIconSources: {
            sortable: IconTableSort,
            ascending: CaretUp,
            descending: CaretDown,
            none: "Minus",
            other: "Activity",
        },
    },
} as xBaseTheme["dataTable"];
